"use client";

import type { QueryClient } from "@tanstack/react-query";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

import { fetchFolders } from "@/lib/api/queries/folders";
import { fetchScrollsPage, type ScrollListParams } from "@/lib/api/queries/scrolls";
import { fetchWorkspaces } from "@/lib/api/queries/workspaces";
import { clientFetch } from "@/lib/api/client-fetch";
import { getClientTimeZone } from "@/lib/activity/local-calendar";
import type { MomentumStats } from "@/components/home/MomentumStrip";
import {
  STALE_TIME_DEFAULT_MS,
  STALE_TIME_MOMENTUM_MS,
  STALE_TIME_PROFILE_MS,
} from "@/lib/react-query/defaults";
import { queryKeys } from "@/lib/react-query/query-keys";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/queries/useCurrentUser";
import { SESSIONS_QUERY_KEY } from "@/hooks/queries/useSessionsQuery";

/** Home dashboard list (folders + fan cards + trash scope). */
export const HOME_SCROLL_LIST_PARAMS = {
  limit: 30,
  archived: "include",
  deleted: "include",
} as const satisfies Partial<ScrollListParams>;

/** Sidebar recent-notes strip. */
export const SIDEBAR_RECENT_SCROLL_PARAMS = {
  limit: 100,
} as const satisfies Partial<ScrollListParams>;

/** Library recents tab default list. */
export const LIBRARY_RECENTS_SCROLL_PARAMS = {
  limit: 40,
} as const satisfies Partial<ScrollListParams>;

/** Trash page — deleted notes list. */
export const TRASH_SCROLL_PARAMS = {
  limit: 40,
  deleted: "only",
} as const satisfies Partial<ScrollListParams>;

async function fetchCurrentUser() {
  const res = await clientFetch("/api/auth/me");
  if (!res.ok) throw new Error("Not authenticated");
  const payload = await res.json();
  return payload.data;
}

async function fetchSessions() {
  const res = await clientFetch("/api/auth/sessions");
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error ?? "sessions");
  return json.data?.sessions ?? [];
}

async function fetchHomeMomentum(
  workspaceId: string | null,
): Promise<MomentumStats> {
  const tz = getClientTimeZone();
  const qs = new URLSearchParams();
  if (workspaceId) qs.set("workspace_id", workspaceId);
  qs.set("tz", tz);
  const res = await clientFetch(`/api/home/momentum?${qs.toString()}`, {
    headers: { "X-User-Timezone": tz },
  });
  if (!res.ok) throw new Error("Failed to load momentum");
  const payload = await res.json();
  return payload.data as MomentumStats;
}

function scrollParamsForWorkspace(
  workspaceId: string,
  base: Partial<ScrollListParams>,
): ScrollListParams {
  return { workspaceId, ...base };
}

async function warmScrollListQueries(
  qc: QueryClient,
  ...paramSets: ScrollListParams[]
): Promise<void> {
  const uniqueKeys = new Set<string>();
  const tasks: Promise<unknown>[] = [];

  for (const params of paramSets) {
    const key = JSON.stringify(queryKeys.scrolls(params));
    if (uniqueKeys.has(key)) continue;
    uniqueKeys.add(key);
    tasks.push(
      qc.prefetchInfiniteQuery({
        queryKey: queryKeys.scrolls(params),
        queryFn: () => fetchScrollsPage(undefined, params),
        initialPageParam: undefined as string | undefined,
        staleTime: STALE_TIME_DEFAULT_MS,
      }),
    );
  }

  await Promise.all(tasks);
}

async function warmWorkspaceListQueries(
  qc: QueryClient,
  workspaceId: string,
  scrollParamSets: ScrollListParams[],
): Promise<void> {
  await Promise.all([
    qc.prefetchQuery({
      queryKey: queryKeys.folders(workspaceId),
      queryFn: () => fetchFolders(workspaceId),
      staleTime: STALE_TIME_DEFAULT_MS,
    }),
    warmScrollListQueries(qc, ...scrollParamSets),
  ]);
}

async function resolveWorkspaceIdForPrefetch(
  qc: QueryClient,
  workspaceId?: string | null,
): Promise<string | null> {
  if (workspaceId) return workspaceId;

  const cached = qc.getQueryData<Awaited<ReturnType<typeof fetchWorkspaces>>>(
    queryKeys.workspaces(),
  );
  const data: Awaited<ReturnType<typeof fetchWorkspaces>> | undefined =
    cached ??
    (await qc.fetchQuery({
      queryKey: queryKeys.workspaces(),
      queryFn: fetchWorkspaces,
      staleTime: STALE_TIME_DEFAULT_MS,
    }));

  return (
    data?.active_workspace_id ??
    data?.workspaces.find((w) => w.is_default)?.id ??
    data?.workspaces[0]?.id ??
    null
  );
}

async function warmHomeWorkspaceQueries(
  qc: QueryClient,
  workspaceId: string,
): Promise<void> {
  const tz = getClientTimeZone();
  await Promise.all([
    warmWorkspaceListQueries(qc, workspaceId, [
      scrollParamsForWorkspace(workspaceId, HOME_SCROLL_LIST_PARAMS),
      scrollParamsForWorkspace(workspaceId, SIDEBAR_RECENT_SCROLL_PARAMS),
    ]),
    qc.prefetchQuery({
      queryKey: queryKeys.homeMomentum(workspaceId, tz),
      queryFn: () => fetchHomeMomentum(workspaceId),
      staleTime: STALE_TIME_MOMENTUM_MS,
    }),
  ]);
}

async function warmLibraryWorkspaceQueries(
  qc: QueryClient,
  workspaceId: string,
): Promise<void> {
  await warmWorkspaceListQueries(qc, workspaceId, [
    scrollParamsForWorkspace(workspaceId, LIBRARY_RECENTS_SCROLL_PARAMS),
    scrollParamsForWorkspace(workspaceId, SIDEBAR_RECENT_SCROLL_PARAMS),
  ]);
}

function prefetchWithWorkspace(
  qc: QueryClient,
  workspaceId: string | null | undefined,
  warm: (qc: QueryClient, wsId: string) => Promise<void>,
): void {
  prefetchProfileRoutes(qc);
  void qc.prefetchQuery({
    queryKey: queryKeys.workspaces(),
    queryFn: fetchWorkspaces,
    staleTime: STALE_TIME_DEFAULT_MS,
  });

  if (workspaceId) {
    void warm(qc, workspaceId);
    return;
  }

  void (async () => {
    try {
      const wsId = await resolveWorkspaceIdForPrefetch(qc, null);
      if (wsId) await warm(qc, wsId);
    } catch {
      /* best-effort */
    }
  })();
}

/** Warm profile cache before settings/billing navigation. */
export function prefetchProfileRoutes(qc: QueryClient): void {
  void qc.prefetchQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    staleTime: STALE_TIME_PROFILE_MS,
  });
}

/** @deprecated Use prefetchProfileRoutes */
export const prefetchSettingsRoutes = prefetchProfileRoutes;

export function prefetchSessionsRoute(qc: QueryClient): void {
  void qc.prefetchQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: fetchSessions,
    staleTime: STALE_TIME_PROFILE_MS,
  });
}

export function prefetchBillingRoute(qc: QueryClient): void {
  prefetchProfileRoutes(qc);
  void clientFetch("/api/billing/snapshot").catch(() => {
    /* best-effort */
  });
}

/**
 * Warm home dashboard queries — workspaces, folders, scrolls, momentum, sidebar recents.
 * Call on sidebar hover / intent before navigating to /home.
 */
export function prefetchHomeRoute(
  qc: QueryClient,
  workspaceId?: string | null,
): void {
  prefetchWithWorkspace(qc, workspaceId, warmHomeWorkspaceQueries);
}

/**
 * Warm library list queries — workspaces, folders, recents scroll list.
 * Call on sidebar hover / intent before navigating to /library.
 */
export function prefetchLibraryRoute(
  qc: QueryClient,
  workspaceId?: string | null,
): void {
  prefetchWithWorkspace(qc, workspaceId, warmLibraryWorkspaceQueries);
}

async function warmTrashWorkspaceQueries(
  qc: QueryClient,
  workspaceId: string,
): Promise<void> {
  await Promise.all([
    warmScrollListQueries(
      qc,
      scrollParamsForWorkspace(workspaceId, TRASH_SCROLL_PARAMS),
    ),
    clientFetch(
      `/api/folders?workspace_id=${encodeURIComponent(workspaceId)}&deleted=only`,
    ).catch(() => {
      /* best-effort — TrashPanel still loads on mount */
    }),
  ]);
}

/**
 * Warm trash list data before navigating to /trash.
 */
export function prefetchTrashRoute(
  qc: QueryClient,
  workspaceId?: string | null,
): void {
  prefetchWithWorkspace(qc, workspaceId, warmTrashWorkspaceQueries);
}

/**
 * Warm Next.js route bundle + optional notebook payload before navigation.
 */
export function prefetchNotebookRoute(
  router: AppRouterInstance,
  notebookId: string,
): void {
  const id = notebookId?.trim();
  if (!id) return;
  try {
    router.prefetch(`/notebook/${id}`);
  } catch {
    /* best-effort */
  }
  void clientFetch(`/api/notebook/${id}`).catch(() => {
    /* best-effort — notebook store hydrates on mount */
  });
}

/** @deprecated Use prefetchHomeRoute */
export const prefetchHomeBootstrap = prefetchHomeRoute;
